import React, { useCallback, useState } from 'react'

import { useActionButton } from 'features/actions/hooks/useActionButton'

import { Box } from 'ui/components/Box'
import { Button, ButtonProps } from 'ui/components/Button'
import { LinkButton, LinkButtonProps } from 'ui/components/LinkButton'

type RecordActionButtonProps = React.ComponentPropsWithoutRef<typeof Button> & {
    action: ActionDto
    record: RecordDto
    includeFields: string[]
}

export const RecordActionButton: React.FC<RecordActionButtonProps> = React.memo(
    ({ action, record, includeFields, onClick, ...props }) => {
        const [isLoading, setIsLoading] = useState(false)
        const { executeAction, navigateTo } = useActionButton({
            action,
            recordId: record._sid,
            record,
            includeFields,
        })

        const to = navigateTo?.isUrlRelative ? navigateTo?.url : undefined
        const href = navigateTo?.isUrlRelative ? undefined : navigateTo?.url

        const handleClick = useCallback(
            async (e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                e.preventDefault()

                setIsLoading(true)
                try {
                    await executeAction()
                } finally {
                    setIsLoading(false)
                }
                onClick?.(e)
            },
            [executeAction, onClick]
        )

        return (
            <Box
                as={
                    (navigateTo?.isNavigationTarget ? LinkButton : Button) as React.ComponentType<
                        ButtonProps & Pick<LinkButtonProps, 'to' | 'href' | 'target'>
                    >
                }
                to={to}
                href={href}
                target={navigateTo?.target}
                onClick={handleClick}
                isLoading={isLoading}
                {...props}
            >
                <Box trim maxWidth="full">
                    {action.name}
                </Box>
            </Box>
        )
    }
)
